/* shamtam-crystal.js — WebGL 3D crystal for entity (gemstone) landing pages.
 * Renders a faceted hexagonal crystal point tinted to the stone's accent.
 * - lazy: each mount inits only when it scrolls into view (IntersectionObserver)
 * - deferred: three.js is dynamic-imported once, on first need (never blocks LCP)
 * - adaptive material per stone (glassy / dark-polished / frosted / metallic)
 * - pauses the render loop when off-screen or the tab is hidden
 * - reduced-motion: one static frame, no spin
 * - graceful: if WebGL/three fails, the CSS placeholder gem stays
 * Mount: <div class="sh-crystal3d" data-hex="#8E5BB5" data-handle="amethyst">
 * Three.js is pinned to a CDN ESM build (dependency flagged to Alex 2026-06-11).
 */
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js';
const METALLIC = new Set(['pyrite', 'hematite', 'goldstone', 'blue-goldstone', 'marcasite']);
const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let threeP = null;
const loadThree = () => (threeP = threeP || import(THREE_URL).then(m => m.default || m));

function makeMaterial(THREE, hex, finish) {
  const col = new THREE.Color(hex);
  const hsl = {}; col.getHSL(hsl);
  const L = 0.299 * col.r + 0.587 * col.g + 0.114 * col.b;
  if (finish === 'metallic') {
    return new THREE.MeshPhysicalMaterial({ color: col, metalness: 0.9, roughness: 0.28,
      clearcoat: 0.6, clearcoatRoughness: 0.2, envMapIntensity: 1.8, flatShading: true });
  }
  if (finish === 'opaque' || L < 0.22) {                 // near-black → polished, stays dark
    return new THREE.MeshPhysicalMaterial({ color: col, metalness: 0, roughness: 0.12,
      transmission: 0.06, thickness: 1.0, ior: 2.3, clearcoat: 1, clearcoatRoughness: 0.04,
      envMapIntensity: 1.5, flatShading: true, transparent: true });
  }
  if (finish === 'frosted' || L > 0.82) {                // pale/white → frosted, faint glow keeps it alive
    return new THREE.MeshPhysicalMaterial({ color: col, metalness: 0, roughness: 0.3,
      transmission: 0.4, thickness: 2.4, ior: 2.2, attenuationColor: col, attenuationDistance: 0.7,
      emissive: col.clone(), emissiveIntensity: 0.05, clearcoat: 1, clearcoatRoughness: 0.12,
      envMapIntensity: 1.3, flatShading: true, transparent: true });
  }
  // glassy gem (default): VIVID — high saturation, deep attenuation + an inner
  // emissive glow so the colour is bright and jewel-like, not washed (Alex 2026-06-11).
  const body = col.clone(); body.setHSL(hsl.h, Math.min(1, hsl.s * 1.7), Math.min(0.56, Math.max(0.42, hsl.l)));
  const att = col.clone(); att.setHSL(hsl.h, 1, hsl.l * 0.75);
  const emi = col.clone(); emi.setHSL(hsl.h, Math.min(1, hsl.s * 1.6), hsl.l * 0.9);
  return new THREE.MeshPhysicalMaterial({ color: body, metalness: 0, roughness: 0.04,
    transmission: 0.55, thickness: 4.2, ior: 2.35, attenuationColor: att, attenuationDistance: 0.38,
    emissive: emi, emissiveIntensity: 0.16, clearcoat: 1, clearcoatRoughness: 0.03,
    envMapIntensity: 1.5, flatShading: true, transparent: true });
}

let SHARED_ENV = null;
function buildEnv(THREE, renderer) {
  if (SHARED_ENV) return SHARED_ENV;
  const cv = document.createElement('canvas'); cv.width = 256; cv.height = 128;
  const x = cv.getContext('2d');
  const g = x.createLinearGradient(0, 0, 0, 128);
  g.addColorStop(0, '#fffdf8'); g.addColorStop(0.4, '#e3d9c8'); g.addColorStop(1, '#b9ab95');
  x.fillStyle = g; x.fillRect(0, 0, 256, 128);
  x.fillStyle = 'rgba(255,255,255,.95)'; x.beginPath(); x.ellipse(70, 40, 34, 20, 0, 0, 7); x.fill();
  x.fillStyle = 'rgba(255,250,240,.5)'; x.beginPath(); x.ellipse(185, 80, 26, 40, 0, 0, 7); x.fill();
  const tex = new THREE.CanvasTexture(cv); tex.mapping = THREE.EquirectangularReflectionMapping;
  const pm = new THREE.PMREMGenerator(renderer);
  SHARED_ENV = pm.fromEquirectangular(tex).texture;
  return SHARED_ENV;
}

async function initGem(mount) {
  const hex = mount.getAttribute('data-hex') || '#7A6FA0';
  const handle = (mount.getAttribute('data-handle') || '').toLowerCase();
  const finish = METALLIC.has(handle) ? 'metallic' : '';
  let THREE;
  try { THREE = await loadThree(); } catch { return; }
  const W = mount.clientWidth || 220, H = mount.clientHeight || 250;
  let renderer;
  try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); }
  catch { return; }                                  // no WebGL → keep placeholder
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(W, H);
  mount.appendChild(renderer.domElement);
  mount.classList.add('is-3d');

  const scene = new THREE.Scene();
  scene.environment = buildEnv(THREE, renderer);
  const cam = new THREE.PerspectiveCamera(34, W / H, 0.1, 100); cam.position.set(0, 0, 6.4);
  const mat = makeMaterial(THREE, hex, finish);
  const R = 0.8;
  const top = new THREE.ConeGeometry(R, 1.05, 6, 1); top.translate(0, 1.3, 0);
  const mid = new THREE.CylinderGeometry(R, R, 1.55, 6, 1);
  const bot = new THREE.ConeGeometry(R, 1.05, 6, 1); bot.rotateZ(Math.PI); bot.translate(0, -1.3, 0);
  const grp = new THREE.Group();
  [top, mid, bot].forEach(g => grp.add(new THREE.Mesh(g, mat)));
  grp.rotation.x = 0.16; grp.rotation.z = 0.04; grp.scale.set(0.98, 0.9, 0.98);
  scene.add(grp);

  // contact shadow
  const sc = document.createElement('canvas'); sc.width = sc.height = 128;
  const sx = sc.getContext('2d');
  const sg = sx.createRadialGradient(64, 64, 4, 64, 64, 60);
  sg.addColorStop(0, 'rgba(60,40,80,.30)'); sg.addColorStop(1, 'rgba(60,40,80,0)');
  sx.fillStyle = sg; sx.fillRect(0, 0, 128, 128);
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(4.2, 2.2),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(sc), transparent: true, depthWrite: false }));
  shadow.position.set(0, -2.35, 0); shadow.rotation.x = -Math.PI / 2.1; scene.add(shadow);

  scene.add(new THREE.AmbientLight(0xffffff, 0.4));
  const key = new THREE.DirectionalLight(0xffffff, 1.7); key.position.set(3, 5, 4); scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.6); fill.position.set(-4, -1, 2); scene.add(fill);
  const spark = new THREE.PointLight(0xffffff, 10, 16); spark.position.set(1.7, 2.6, 2.8); scene.add(spark);

  let raf = null, visible = true;
  const draw = () => { renderer.render(scene, cam); };
  const tick = () => { if (!REDUCED) grp.rotation.y += 0.011; draw(); raf = requestAnimationFrame(tick); };
  const start = () => { if (raf == null && visible && !document.hidden) { if (REDUCED) draw(); else tick(); } };
  const stop = () => { if (raf != null) { cancelAnimationFrame(raf); raf = null; } };
  const io = new IntersectionObserver(es => { visible = es[0].isIntersecting; visible ? start() : stop(); }, { threshold: 0.05 });
  io.observe(mount);
  document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
  start();
}

function boot() {
  const mounts = document.querySelectorAll('.sh-crystal3d[data-hex]:not(.is-3d)');
  if (!mounts.length) return;
  // The crystal is the hero centrepiece (always above the fold), so we init on
  // idle rather than gating on IntersectionObserver — IO can be unreliable in
  // some environments, and three.js is already deferred by the dynamic import.
  const go = () => mounts.forEach(initGem);
  if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 1500 });
  else setTimeout(go, 300);
}
if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);
